import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "../styles/presale.css";

/** The ladder as the desktop app sells it. Prices and caps mirror PRESALE_TIER_LADDER
 *  in Ego Desktop's wallet — change them in both places or the two will disagree. */
const RUNGS = [
  { name: "Seed", price: 0.008, cap: 40_000_000, state: "open" },
  { name: "Private", price: 0.012, cap: 40_000_000, state: "next" },
  { name: "Launch", price: 0.02, cap: null, state: "later" },
];

const LAUNCH = RUNGS[RUNGS.length - 1].price;
const MULTIPLE = LAUNCH / RUNGS[0].price;

function useSeen() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => e.isIntersecting && setSeen(true),
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, seen];
}

/** Counts to `to` once the band is on screen, and lands on it immediately for anyone
 *  who has asked for less motion. */
function Tally({ to, decimals = 0, prefix = "", suffix = "" }) {
  const [n, setN] = useState(to);
  const [ref, seen] = useSeen();

  useEffect(() => {
    if (!seen) return undefined;
    const still =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (still) {
      setN(to);
      return undefined;
    }
    let raf;
    const t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / 900);
      setN(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [seen, to]);

  return (
    <span ref={ref}>
      {prefix}
      {n.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })}
      {suffix}
    </span>
  );
}

export default function PresaleBand() {
  return (
    <section className="ps-band" id="presale">
      <div className="ps-inner">
        <motion.div
          className="ps-head"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
        >
          <span className="ps-tag">
            <span className="ps-pulse" />
            Seed round open
          </span>
          <h2 className="ps-title">
            The price only moves one way<br />
            <em>before</em> the network opens.
          </h2>
          <p className="ps-lead">
            EGOC is on sale inside Ego Desktop while the testnet runs. Three rungs,
            published up front — what you pay depends on which one is still standing
            when you arrive.
          </p>
        </motion.div>

        <div className="ps-ladder" role="list">
          {RUNGS.map((r, i) => (
            <motion.div
              key={r.name}
              role="listitem"
              className={`ps-rung is-${r.state}`}
              initial={{ opacity: 0, y: 26 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.55, delay: 0.08 * i }}
            >
              <div className="ps-rung-top">
                <span className="ps-rung-name">{r.name}</span>
                {r.state === "open" && <span className="ps-chip">Open now</span>}
                {r.state === "next" && <span className="ps-chip muted">Next</span>}
                {r.state === "later" && <span className="ps-chip muted">At launch</span>}
              </div>

              <div className="ps-price">
                <Tally to={r.price} decimals={3} prefix="$" />
              </div>

              {/* one scale across all three, so the gap is something you see */}
              <div className="ps-track" aria-hidden="true">
                <motion.span
                  className="ps-fill"
                  initial={{ width: 0 }}
                  whileInView={{ width: `${(r.price / LAUNCH) * 100}%` }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 1, delay: 0.15 + 0.08 * i, ease: [0.22, 1, 0.36, 1] }}
                />
              </div>

              <div className="ps-rung-foot">
                {r.cap
                  ? `${(r.cap / 1_000_000).toFixed(0)}M EGOC available`
                  : "Open market price"}
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="ps-facts"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.55, delay: 0.1 }}
        >
          <div className="ps-fact">
            <span className="ps-fact-n">
              <Tally to={MULTIPLE} decimals={1} suffix="×" />
            </span>
            <span className="ps-fact-c">Seed price to launch price</span>
          </div>
          <div className="ps-fact">
            <span className="ps-fact-n">
              <Tally to={80} suffix="M" />
            </span>
            <span className="ps-fact-c">EGOC across both rounds — 8% of supply</span>
          </div>
          <div className="ps-fact">
            <span className="ps-fact-n">
              <Tally to={51} suffix="%" />
            </span>
            <span className="ps-fact-c">Never sold — earned by nodes and validators</span>
          </div>
        </motion.div>

        <motion.div
          className="ps-cta"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Link
            to="/download"
            className="btn"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            <button>Get Ego Desktop</button>
          </Link>
          <p className="ps-where">
            The sale runs inside the app — open <b>Wallet → Presale</b> once it is installed.
            Your coins land in a wallet only you hold the keys to.
          </p>
        </motion.div>
      </div>
    </section>
  );
}
